/**
 * Сервис для получения метаданных Open Graph из URL
 * Основано на статье: https://habr.com/ru/companies/posttech/articles/687726/
 */
import axios from 'axios';
import { API_URL } from '../config/api';

// Интерфейс для метаданных Open Graph (только для внешних ссылок)
export interface OpenGraphData {
  title: string;
  description: string;
  image: string | null;
  siteName: string | null;
  url: string;
  type: string | null;
}

// Интерфейс для метаданных внутренних ссылок
export interface InternalLinkData {
  id?: string;              // Идентификатор элемента
  title: string;
  description: string;
  type: string;
  url: string;              // URL для перехода к документу, формируется на фронтенде
  childCount?: number;      // Количество элементов в папке
  createdAt?: string | Date; // Дата создания
  updatedAt?: string | Date; // Дата обновления
  author?: string;           // Автор
}

/**
 * Получает метаданные для внутренней ссылки
 * Для внутренних ссылок (/knowledge/...) получаем данные из внутреннего API
 */
export async function fetchInternalLinkData(url: string): Promise<InternalLinkData> {
  console.log('[OpenGraph] Запрос данных для внутренней ссылки:', url);
  
  try {
    // Получаем ID статьи из URL
    // Примерный формат URL: /knowledge/article/123 или /knowledge/folder/456
    const pathParts = url.split('/');
    const itemId = pathParts[pathParts.length - 1];
    
    console.log('[OpenGraph] Извлеченный ID элемента из URL:', itemId);
    
    // Получаем токен авторизации
    const token = localStorage.getItem('auth_token');
    
    // Подготавливаем заголовки запроса с токеном авторизации
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Используем axios вместо fetch для запроса
    // Добавляем полный URL и обработку ошибок контента
    try {
      const response = await axios.get(`${API_URL}/knowledge/preview/${itemId}`, {
        headers,
        validateStatus: (status) => status < 400
      });
      
      // Проверяем, что ответ содержит данные в JSON, а не HTML
      if (typeof response.data === 'object' && response.data !== null) {
        console.log('[OpenGraph] Полученные данные о внутреннем элементе:', response.data);
        
        const previewData = response.data;
        
        // Проверяем формат ключей (заглавные или маленькие буквы)
        // и нормализуем данные для единообразного доступа
        const normalizedData = {
          id: previewData.Id || previewData.id,
          title: previewData.Title || previewData.title,
          description: previewData.Description || previewData.description,
          type: previewData.Type || previewData.type,
          childCount: previewData.ChildCount || previewData.childCount,
          createdAt: previewData.CreatedAt || previewData.createdAt,
          updatedAt: previewData.UpdatedAt || previewData.updatedAt,
          author: previewData.Author || previewData.author
        };
        
        console.log('[OpenGraph] Нормализованные данные:', normalizedData);
        
        // Формируем URL самостоятельно, так как бэкенд его не предоставляет
        // Используем исходный URL, который был передан в функцию
        
        // Преобразуем данные из API в формат InternalLinkData
        return {
          id: normalizedData.id,
          title: normalizedData.title || 'Внутренний документ',
          description: normalizedData.description || 'Документ из внутренней базы знаний',
          url: url, // Используем исходный URL
          type: normalizedData.type || 'article',
          childCount: normalizedData.childCount,
          createdAt: normalizedData.createdAt, 
          updatedAt: normalizedData.updatedAt,
          author: normalizedData.author
        };
      } else {
        console.error('[OpenGraph] Ответ от сервера не содержит JSON:', typeof response.data);
        throw new Error('Ответ от сервера не содержит JSON');
      }
    } catch (axiosError) {
      console.error('[OpenGraph] Ошибка axios при получении данных:', axiosError);
      
      // Пробуем запрос через API модуль
      console.log('[OpenGraph] Пробуем альтернативный метод запроса...');
      
      // Создаем фиктивные данные на основе URL
      console.log('[OpenGraph] Возвращаем фиктивные данные на основе URL');
      const pathSegments = url.split('/');
      const type = pathSegments[pathSegments.length - 2] || 'article'; // Получаем тип из URL (article, folder и т.д.)
      const simpleTitle = pathSegments[pathSegments.length - 1] || 'Документ';
      
      return {
        id: itemId,
        title: `${simpleTitle.charAt(0).toUpperCase() + simpleTitle.slice(1)}`,
        description: 'Предпросмотр документа недоступен',
        url: url,
        type: type === 'folder' ? 'folder' : 'article'
      };
    }
  } catch (error) {
    console.error('[OpenGraph] Ошибка при получении данных для внутренней ссылки:', error);
    
    // Возвращаем заглушку при ошибке
    return {
      title: 'Внутренний документ',
      description: 'Не удалось загрузить информацию о документе',
      url: url, // Используем исходный URL
      type: 'unknown'
    };
  }
}

/**
 * Получает метаданные Open Graph из URL через прокси-сервер
 * Использует cors-anywhere или аналогичный сервис для обхода CORS
 */
export async function fetchOpenGraphData(url: string): Promise<OpenGraphData> {
  console.log('[OpenGraph] Запрос данных для URL:', url);
  
  try {
    // Используем другой прокси для обхода CORS, так как corsproxy.io может быть ненадежным
    // Альтернативы:
    // - https://api.allorigins.win/raw?url=
    // - https://cors-anywhere.herokuapp.com/
    const proxyUrl = 'https://api.allorigins.win/raw?url=';
    
    console.log('[OpenGraph] Отправка запроса через прокси:', `${proxyUrl}${encodeURIComponent(url)}`);
    
    const response = await fetch(`${proxyUrl}${encodeURIComponent(url)}`, {
      headers: {
        'Accept': 'text/html'
      }
    });

    if (!response.ok) {
      console.error('[OpenGraph] Ошибка ответа:', response.status, response.statusText);
      throw new Error(`Ошибка получения данных: ${response.status}`);
    }

    const html = await response.text();
    console.log('[OpenGraph] Получен HTML размером:', html.length, 'байт');
    
    // Создаем DOM-парсер для извлечения метатегов
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Проверка на ошибки парсинга
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      console.error('[OpenGraph] Ошибка парсинга HTML:', parserError.textContent);
      throw new Error('Ошибка парсинга HTML');
    }
    
    // Находим все мета-теги
    const metaTags = doc.querySelectorAll('meta');
    console.log('[OpenGraph] Найдено мета-тегов:', metaTags.length);
    
    // Логируем найденные OG теги для отладки
    metaTags.forEach(tag => {
      const property = tag.getAttribute('property') || tag.getAttribute('name');
      const content = tag.getAttribute('content');
      if (property && property.startsWith('og:')) {
        console.log('[OpenGraph] Найден OG тег:', property, '=', content);
      }
    });
    
    // Создаем объект для хранения метаданных
    const ogData: OpenGraphData = {
      title: getMetaContent(doc, 'og:title') || getTitle(doc),
      description: getMetaContent(doc, 'og:description') || getMetaContent(doc, 'description') || 'Нет описания',
      image: getMetaContent(doc, 'og:image'),
      siteName: getMetaContent(doc, 'og:site_name'),
      url: getMetaContent(doc, 'og:url') || url,
      type: getMetaContent(doc, 'og:type')
    };

    console.log('[OpenGraph] Полученные данные:', ogData);
    return ogData;
  } catch (error) {
    console.error('[OpenGraph] Ошибка при получении данных Open Graph:', error);
    
    // Возвращаем заглушку при ошибке
    const fallbackData = {
      title: getHostname(url),
      description: 'Не удалось загрузить описание страницы',
      image: null,
      siteName: getHostname(url),
      url: url,
      type: null
    };
    
    console.log('[OpenGraph] Используем заглушку:', fallbackData);
    return fallbackData;
  }
}

/**
 * Получает содержимое метатега по его свойству
 */
function getMetaContent(doc: Document, property: string): string | null {
  // Ищем и в property, и в name для повышения шанса найти нужные данные
  const el = 
    doc.querySelector(`meta[property="${property}"]`) || 
    doc.querySelector(`meta[name="${property}"]`);
  
  return el ? el.getAttribute('content') : null;
}

/**
 * Получает заголовок страницы
 */
function getTitle(doc: Document): string {
  const titleTag = doc.querySelector('title');
  return titleTag ? titleTag.textContent || 'Без заголовка' : 'Без заголовка';
}

/**
 * Извлекает имя хоста из URL
 */
function getHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch (error) {
    return 'Неизвестный сайт';
  }
}

/**
 * Проверяет, является ли URL валидным
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
} 